<script lang="ts">
	import { getContext } from 'svelte';
	import { searchTagsForString } from '../../../api/EnzianDataApi';
	import DocumentTag from '../../../view/document/DocumentTag.svelte';
	import { clickOutside, debounce } from '../../../helper/Extensions';
	import { handleStoreError } from '../../../stores/GeneralStore';
	import DefaultTextInput from '../../input/DefaultTextInput.svelte';
	import {
		buildFilterValue,
		FilterCondition,
		FilterType,
		type FilterOption,
		type SimpleListContext
	} from '../../../types/ListTypes';

	const { updateFilterActivityState } = getContext<SimpleListContext>('enzianListContext');

	let stringValue: string = $state('');
	let tags: string[] = $state([]);
	let tagSugestions: string[] = $state([]);
	let filterType: FilterCondition = FilterCondition.CO;

	interface Props {
		//The whole information with EQ
		filterOption: FilterOption;
	}

	let { filterOption = $bindable() }: Props = $props();

	function handleFilterChange() {
		if (tags.length > 0) {
			buildFullFilterValue();
			updateFilterActivityState();
			stringValue = '';
		} else {
			//Update the external value.
			filterOption.value = null;
			updateFilterActivityState();
		}
	}

	function buildFullFilterValue() {
		//Update the external value.
		filterOption.value = buildFilterValue(tags.join('&'), filterType, FilterType.AND);
	}

	function removeTag(tagToRemove: string) {
		tags = tags.filter((tag) => tag != tagToRemove);

		if (tags.length > 0) {
			buildFullFilterValue();
			updateFilterActivityState();
		} else {
			//Update the external value.
			filterOption.value = null;
			updateFilterActivityState();
		}
	}

	const debouncedSuggestionSearch = debounce(async (event: CustomEvent) => {
		const target = event.target as HTMLInputElement;
		//Filter the sugestions
		try {
			tagSugestions = await searchTagsForString(target.value);
		} catch (err) {
			handleStoreError('documentview.err_load_tag_suggestions', err);
		}
	});

	function parseString(rawValue: string | null): string[] {
		//Read value back in.
		if (rawValue != null && rawValue != 'null') {
			const values = rawValue.split('|');

			if (values.length >= 2) {
				return values[0].split('&');
			} else {
				return [];
			}
		} else {
			return [];
		}
	}

	$effect(() => {
		tags = parseString(filterOption.value);
	});
</script>

<div class="relative">
	<DefaultTextInput propertyName="" onKeyup={debouncedSuggestionSearch} bind:value={stringValue} />

	{#if tagSugestions.length > 0}
		<div
			use:clickOutside={() => {
				tagSugestions = [];
			}}
			class="absolute right-0 top-0 flex flex-col gap-2 p-2 bg-white z-30 rounded-md border-2"
		>
			{#each tagSugestions as tag}
				<DocumentTag
					allowDelete={false}
					onClick={() => {
						//Add it to the tags
						tags = [...tags, tag];
						//Clear the input
						stringValue = '';
						//Clear the suggestions
						tagSugestions = [];
						//Make sure that we send the new value
						handleFilterChange();
					}}
					value={tag}
				/>
			{/each}
		</div>
	{/if}

	<div class="flex gap-2 overflow-x-auto py-2">
		{#each tags as tag}
			<DocumentTag allowDelete={true} onRequestDelete={removeTag} value={tag} />
		{/each}
	</div>
</div>
